import React, { useState, useEffect } from "react";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import EventTable from "../Components/EventTable";
import { useLoginResult } from "../State/LoginResultContext";
import { useEventResult } from "../State/EventResultContext";
import { useNavigate } from "react-router-dom";
import { useErrorRenderer } from "../Hooks/useErrorRenderer";
import { useMinWidth } from "../Hooks/useMinWidth";
import { API_URL } from "../utils/constants";
import { showToast } from "../utils/toast";

import EventTableSkeleton from "../Components/EventTableSkeleton";
import ShareEventsModal from "../Components/ShareEventsModal";
import packageJson from "../../package.json";

function ManageEventsMobileEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12">
      <div className="relative mb-8 inline-block">
        <CalendarDaysIcon
          className="mx-auto h-[7.25rem] w-[7.25rem] text-white/95"
          strokeWidth={1.15}
          aria-hidden
        />
        <span
          className="absolute -right-2 top-0 rotate-[-14deg] font-light tracking-widest text-[1rem] text-white/60"
          aria-hidden
        >
          zzz
        </span>
      </div>
      <p className="text-center text-lg font-bold text-white">
        No upcoming events.
      </p>
      <p className="mt-2 max-w-[280px] text-center text-[0.95rem] leading-snug text-white/85 atkinson-font">
        Any new events will appear here.
      </p>
    </div>
  );
}

const OrgEvents = () => {
  const [message, setMessage, isError, setIsError, resetErrorState] =
    useErrorRenderer();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setOrgId] = useState("");
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const smUp = useMinWidth(640);
  const { loginResults } = useLoginResult();
  const { updateeventResults } = useEventResult();
  const navigate = useNavigate();

  const handleStreamEvent = (eventId) => {
    const event = events.find((evt) => evt.id === eventId);
    updateeventResults(event);
    navigate("/eventdetails");
  };

  const handleEditEvent = (event) => {
    updateeventResults(event);
    navigate("/events", { state: { mode: "edit" } });
  };

  useEffect(() => {
    if (loginResults.token) {
      const fetchOrgEvents = async () => {
        setLoading(true);
        var token = "";
        if (loginResults !== null) {
          token = loginResults.token;
        }

        try {
          setOrgId(loginResults.activeOrganization.id);
          const response = await fetch(
            API_URL +
              `/events/${loginResults.activeOrganization.id}/org-events`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );

          const data = await response.json();
          if (response.status === 200) {
            setEvents(data);
          } else {
            setMessage(data.message || "please check your network connection");
            setIsError(true);
            resetErrorState();
          }
        } catch (error) {
          setMessage(error.message || "please check your network connection");
          setIsError(true);
          resetErrorState();
        } finally {
          setLoading(false);
        }
      };
      fetchOrgEvents();
    }
  }, [loginResults.token]);

  const handleDelete = async (eventId) => {
    var token = "";
    if (loginResults !== null) {
      token = loginResults.token;
    }

    try {
      const response = await fetch(
        API_URL + `/events/${eventId}/take-off-list`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (response.status === 200) {
        setEvents(events.filter((event) => event.id !== eventId));
        showToast("Event removed.", { type: "success" });
      } else {
        setMessage(data.message);
        setIsError(true);
        resetErrorState();
      }
    } catch (error) {
      setMessage(error.message);
      setIsError(true);
      resetErrorState();
    }
  };

  const createEvent = (e) => {
    e.preventDefault();
    navigate("/events");
  };

  const renderEventsBody = () => {
    if (loading) {
      return <EventTableSkeleton />;
    }
    if (events.length > 0) {
      return (
        <EventTable
          events={events}
          onStreamEvent={handleStreamEvent}
          onDelete={handleDelete}
          onEdit={handleEditEvent}
        />
      );
    }
    return (
      <>
        <div className="sm:hidden">
          <ManageEventsMobileEmptyState />
        </div>
        <h4 className="my-4 hidden text-center text-xl text-gray-400 sm:block">
          No events found.
        </h4>
      </>
    );
  };

  return (
    <div className="text-white font-sans pb-10">
      <div className="mx-auto max-w-[85rem] px-5 sm:px-4">
        {!smUp ? (
          <div className="flex min-h-[calc(100dvh-10rem)] flex-col">
            <div className="pt-6 text-center">
              <h1 className="text-[32px] font-bold atkinson-font">
                Manage Events
              </h1>
              <p className="mx-auto mt-2 max-w-md text-[0.95rem] leading-snug text-white atkinson-font">
                Create, start or edit events.
              </p>
              <button
                type="button"
                style={{ border: "2px solid #6A6680" }}
                className="mt-6 w-full rounded-[10px] bg-[#5701AE] py-[12px] text-base font-semibold text-white shadow-[0_0_0_1px_rgba(255,255,255,0.12)] transition-colors hover:bg-[#7c4dff] atkinson-font"
                onClick={createEvent}
              >
                Create Event
              </button>
            </div>

            <div className="mt-10 flex flex-1 flex-col">
              <h2 className="mb-3 text-left text-xl font-bold text-white atkinson-font">
                Your Events
              </h2>
              <div className="min-h-0 flex-1">{renderEventsBody()}</div>
            </div>

            <footer className="mt-10 border-t border-white/20 pt-8">
              <button
                type="button"
                style={{ border: "2px solid rgba(255,255,255,0.6)" }}
                className="w-full rounded-[10px] !bg-transparent py-[12px] text-base font-semibold text-white transition-colors hover:bg-white/10 atkinson-font"
                onClick={() => setShareModalOpen(true)}
              >
                Share Your Events
              </button>
              {/* <p className="mt-6 text-center text-[13px] text-white/45 atkinson-font">
                Version {packageJson.version}
              </p> */}
            </footer>
          </div>
        ) : (
          <>
            <div className="mb-6 pt-8">
              <div className="grid grid-cols-6 items-center gap-6">
                <div className="col-span-3 text-left">
                  <h1 className="text-[40px] font-bold atkinson-font">
                    Manage Events
                  </h1>
                  <p className="mt-1 text-base text-white atkinson-font">
                    Create, start, or share your live captions
                  </p>
                </div>

                <div className="col-span-3 flex flex-row justify-end gap-3">
                  <button
                    type="button"
                    style={{ border: "2px solid rgba(255,255,255,0.55)" }}
                    className="rounded-lg bg-transparent py-[10px] px-6 text-base font-semibold text-white shadow-[0_0_0_1px_rgba(255,255,255,0.12)] transition-colors hover:bg-white/10 atkinson-font sm:min-w-[200px]"
                    onClick={() => setShareModalOpen(true)}
                  >
                    Share Your Events
                  </button>
                  <button
                    type="button"
                    style={{
                      border: "2px solid #6A6680",
                    }}
                    className="rounded-lg bg-[#5701AE] py-[10px] px-8 text-base font-semibold text-white shadow-[0_0_0_1px_rgba(255,255,255,0.2)] transition-colors hover:bg-[#7c4dff] atkinson-font sm:min-w-[200px]"
                    onClick={createEvent}
                  >
                    Create Event
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 min-w-[300px] w-full rounded-[20px] border border-[#4a148c] bg-[#00000070] p-4 shadow-lg sm:p-6">
                <h2 className="mb-4 text-[32px] font-bold atkinson-font">
                  Recent Events
                </h2>
                {renderEventsBody()}
              </div>
            </div>
          </>
        )}
      </div>

      <ShareEventsModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={loginResults?.permalink}
      />
    </div>
  );
};

export default OrgEvents;
